import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import "./create-course.dialog.scoped.scss";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  TextField,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { CheckIcon } from "@mantine/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";
import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import MomentUtils from "@date-io/moment";
import moment, { Moment } from "moment";
import { LangService } from "../../../../services/lang/lang.service";
import { LinearProgress } from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { icons } from "../../../../statics/icons";
import { UserRoles } from "../../../../types/userRoles";
import AppSwitchComponent from "../../../../components/form/app-switch";
import { services } from "../../../../services";
import AppCheckboxComponent from "../../../../components/form/app-checkbox";
import AppButtonComponent from "../../../../components/form/app-button";
import AppRadioComponent from "../../../../components/form/app-radio";

// Constants
const FIELD_LIMITS = {
  TITLE: 80,
  SUBTITLE: 250,
  DESCRIPTION: 1200,
} as const;

const IMAGE_CONSTRAINTS = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ["image/jpeg", "image/jpg", "image/png"],
  ALLOWED_EXTENSIONS: ["jpg", "jpeg", "png"],
} as const;

function toScheduleMoment(
  date: Moment | null,
  time: Moment | null,
): Moment | null {
  if (!date || !moment(date).isValid()) return null;
  if (time && moment(time).isValid()) {
    return moment(date)
      .clone()
      .hour(moment(time).hour())
      .minute(moment(time).minute());
  }
  return moment(date).clone();
}

const ROLE_GROUP_OPTIONS = [
  {
    role: UserRoles.COORDINATOR,
    group: "INTERNAL",
    label: "ROLES.COORDINATOR",
  },
  { role: UserRoles.EDITOR, group: "INTERNAL", label: "ROLES.EDITOR_INTERNAL" },
  { role: UserRoles.EDITOR, group: "EXTERNAL", label: "ROLES.EDITOR_EXTERNAL" },
  {
    role: UserRoles.AUDITOR,
    group: "INTERNAL",
    label: "ROLES.AUDITOR_INTERNAL",
  },
  {
    role: UserRoles.AUDITOR,
    group: "EXTERNAL",
    label: "ROLES.AUDITOR_EXTERNAL",
  },
  {
    role: UserRoles.CONTRIBUTOR,
    group: "INTERNAL",
    label: "ROLES.CONTRIBUTOR_INTERNAL",
  },
  {
    role: UserRoles.CONTRIBUTOR,
    group: "EXTERNAL",
    label: "ROLES.CONTRIBUTOR_EXTERNAL",
  },
] as const;

// Types
interface CreateCourseDialogProps {
  show: boolean;
  onClose: (success?: boolean) => void;
  dialogClassName?: string;
  courseId?: number;
  courseData?: any;
}

interface RoleGroup {
  role: string;
  group: string;
}

interface CourseFormData {
  title: string;
  brief: string;
  description: string;
  imageKey: string;
  categoryId: number;
  status: string;
  publishedDateTime: string;
  unpublishedDateTime: string;
  userGroups: RoleGroup[];
  sequential: string;
}

interface FormErrors {
  title?: string;
  brief?: string;
  description?: string;
  categoryId?: string;
  publishedDateTime?: string;
  unpublishedDateTime?: string;
  [key: string]: string | undefined;
}

// Styles
const useStyles = makeStyles(() => ({
  datePackerInputRoot: {
    backgroundColor: "var(--light)",
    height: "100%",
    paddingTop: 0,
    margin: "0 !important",
  },
  datePackerInputAdornment: {
    margin: "0 !important",
  },
}));

const CreateCourseDialog: React.FC<CreateCourseDialogProps> = ({
  show,
  onClose,
  dialogClassName,
  courseId,
  courseData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  // Services - memoized to avoid recreation
  const langService = useMemo(() => new LangService(), []);

  // Locale and date setup
  const locale = useMemo(() => langService.getCurrentLang(), [langService]);
  const minDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  // Set moment locale
  React.useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  // Form state
  const [formData, setFormData] = useState<CourseFormData>({
    title: "",
    brief: "",
    description: "",
    categoryId: 0,
    status: "ACTIVE",
    publishedDateTime: "",
    unpublishedDateTime: "",
    imageKey: "",
    userGroups: [],
    sequential: "NON_SEQUENTIAL",
  });

  const [courseCategories, setCourseCategories] = useState<any[]>([]);

  // Date/Time state for publish
  const [publishDate, setPublishDate] = useState<Moment | null>(null);
  const [publishTime, setPublishTime] = useState<Moment | null>(null);

  // Date/Time state for unpublish
  const [unpublishDate, setUnpublishDate] = useState<Moment | null>(null);
  const [unpublishTime, setUnpublishTime] = useState<Moment | null>(null);

  // UI state
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined,
  );
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const isEditMode = useMemo(() => Boolean(courseId && courseData), [
    courseId,
    courseData,
  ]);

  /**
   * Parse datetime string and split into date and time
   */
  const parseDateTime = useCallback((dateTimeString: string) => {
    if (!dateTimeString) return { date: null, time: null };

    const momentDate = moment(dateTimeString);
    if (!momentDate.isValid()) return { date: null, time: null };

    return {
      date: momentDate,
      time: momentDate,
    };
  }, []);

  /**
   * Load course data into form when in edit mode
   */
  useEffect(() => {
    if (isEditMode && courseData) {
      // Parse published datetime
      const publishDateTime = parseDateTime(courseData.publishedDateTime);
      setPublishDate(publishDateTime.date);
      setPublishTime(publishDateTime.time);

      // Parse unpublished datetime
      const unpublishDateTime = parseDateTime(courseData.unpublishedDateTime);
      setUnpublishDate(unpublishDateTime.date);
      setUnpublishTime(unpublishDateTime.time);

      // Store original image key
      const existingImageKey = courseData.imageURL || courseData.imageKey || "";

      // Format datetime strings to match create format (YYYY-MM-DD HH:mm)
      const formattedPublishDateTime =
        publishDateTime.date && publishDateTime.time
          ? combineDateTime(publishDateTime.date, publishDateTime.time)
          : courseData.publishedDateTime || "";

      const formattedUnpublishDateTime =
        unpublishDateTime.date && unpublishDateTime.time
          ? combineDateTime(unpublishDateTime.date, unpublishDateTime.time)
          : courseData.unpublishedDateTime || "";

      // Set form data
      setFormData({
        title: courseData.title || "",
        brief: courseData.brief || "",
        description: courseData.description || "",
        categoryId: courseData.categoryId || courseData.category?.id || 0,
        status: courseData.status || "ACTIVE",
        publishedDateTime: formattedPublishDateTime,
        unpublishedDateTime: formattedUnpublishDateTime,
        imageKey: existingImageKey,
        userGroups: courseData.userGroups || [],
        sequential: courseData.sequential || "NON_SEQUENTIAL",
      });

      console.log(courseData);
      // Set existing image preview if available
      if (existingImageKey) {
        setImagePreview(existingImageKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, courseData, parseDateTime]);

  /**
   * Get image preview from file
   */
  const getImagePreview = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        resolve(event.target.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Validate image file
   */
  const validateImageFile = useCallback(
    (file: File): string | null => {
      // Check file type
      if (!IMAGE_CONSTRAINTS.ALLOWED_TYPES.includes(file.type as any)) {
        return (
          t("EDUCATION_PAGE.INVALID_IMAGE_TYPE") ||
          "Only JPG, JPEG, and PNG files are allowed"
        );
      }

      // Check file size
      if (file.size > IMAGE_CONSTRAINTS.MAX_SIZE) {
        const maxSizeMB = IMAGE_CONSTRAINTS.MAX_SIZE / 1024 / 1024;
        return (
          t("EDUCATION_PAGE.IMAGE_TOO_LARGE") ||
          `Image size must be less than ${maxSizeMB}MB`
        );
      }

      return null;
    },
    [t],
  );

  /**
   * Format title to only contain letters and underscores
   * Replaces spaces and special characters with underscores
   */
  const formatTitleForId = useCallback((title: string): string => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^a-z_]/g, "_") // Replace everything that's not a letter or underscore with underscore
      .replace(/_+/g, "_") // Replace multiple consecutive underscores with single underscore
      .replace(/^_+|_+$/g, ""); // Remove leading and trailing underscores
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Upload image to signed URL
   * Returns the imageKey (URL) on success, or null on failure
   */
  const uploadImage = useCallback(
    async (file: File, courseTitle: string): Promise<string | null> => {
      setUploadingImage(true);
      setUploadProgress(0);

      try {
        // Get file extension
        const fileExtension =
          file.name.split(".").pop()?.toLowerCase() || "jpg";
        const fileName = `course-${Date.now()}.${fileExtension}`;

        const formattedId =
          formatTitleForId(courseTitle) || `course_${Date.now()}`;
        // Request signed URL
        const { signedUrl, url } = await services.lookups.requestSignedUrl(
          fileName,
          "courses",
          formattedId,
        );

        if (!signedUrl || !url) {
          throw new Error("Failed to get signed URL");
        }

        // Upload to signed URL
        const uploadResult = await services.lookups.uploadToSignedURL(
          signedUrl,
          file,
          (progressEvent: { loaded: number; total: number }) => {
            const progress = Math.round(
              (100 * progressEvent.loaded) / progressEvent.total,
            );
            setUploadProgress(progress);
          },
        );

        if (uploadResult === 1) {
          // Success - return the URL
          return url;
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        showToastMsg(
          "error",
          t("EDUCATION_PAGE.IMAGE_UPLOAD_ERROR") ||
            "Failed to upload image. Please try again.",
        );
        return null;
      } finally {
        setUploadingImage(false);
        setUploadProgress(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [services.lookups, t],
  );

  /**
   * Handle file input change
   */
  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file
      const error = validateImageFile(file);
      if (error) {
        showToastMsg("error", error);
        // Reset input
        if (inputFileRef.current) {
          inputFileRef.current.value = "";
        }
        return;
      }

      setImageFile(file);
      const preview = await getImagePreview(file);
      setImagePreview(preview);

      setFormData((prev) => ({
        ...prev,
        imageKey: "",
      }));

      setErrors((prev) => ({
        ...prev,
        imageKey: "",
      }));
    },
    [validateImageFile, getImagePreview],
  );

  /**
   * Remove image
   */
  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      imageKey: "",
    }));

    // Set error when image is removed
    const error = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    setErrors((prev) => ({
      ...prev,
      imageKey: error,
    }));

    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  }, [t]);

  /**
   * Trigger file input click
   */
  const handleImageClick = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  /**
   * Combine date and time into a single datetime string
   */
  const combineDateTime = useCallback(
    (date: Moment | null, time: Moment | null): string => {
      if (!date) return "";
      if (!time) {
        // If no time, use midnight
        return moment(date).format("YYYY-MM-DD HH:mm");
      }
      return moment(date)
        .hour(moment(time).hour())
        .minute(moment(time).minute())
        .format("YYYY-MM-DD HH:mm");
    },
    [],
  );

  /**
   * Validate form field
   */
  const validateField = useCallback(
    (name: keyof CourseFormData, value: any): string => {
      switch (name) {
        case "title":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.COURSE_NAME_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.TITLE) {
            return `Title must be less than ${FIELD_LIMITS.TITLE} characters`;
          }
          return "";
        case "brief":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.SUBTITLE_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.SUBTITLE) {
            return `Subtitle must be less than ${FIELD_LIMITS.SUBTITLE} characters`;
          }
          return "";
        case "description":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.DESCRIPTION_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.DESCRIPTION) {
            return `Description must be less than ${FIELD_LIMITS.DESCRIPTION} characters`;
          }
          return "";
        case "categoryId":
          if (!value || value === 0) {
            return t("EDUCATION_PAGE.CATEGORY_REQUIRED");
          }
          return "";
        case "imageKey":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.IMAGE_REQUIRED");
          }
          return "";
        default:
          return "";
      }
    },
    [t],
  );

  /**
   * Handle text input changes
   */
  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | { name?: string; value: unknown }
      >,
    ) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof CourseFormData;
      const stringValue = typeof value === "string" ? value : String(value);

      setFormData((prev) => ({ ...prev, [fieldName]: stringValue }));

      // Validate field
      const error = validateField(fieldName, stringValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  /**
   * Handle select dropdown changes
   * Select component has different onChange signature: (event, value)
   */
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof CourseFormData;
      const numValue = typeof value === "number" ? value : Number(value);

      setFormData((prev) => ({ ...prev, [fieldName]: numValue }));

      // Validate field
      const error = validateField(fieldName, numValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  /**
   * Handle role/group checkbox change
   */
  const handleRoleGroupChange = useCallback(
    (role: string, group: string, checked: boolean) => {
      setFormData((prev) => {
        const currentUserGroups = prev.userGroups || [];

        if (checked) {
          // Add role/group if checked
          const exists = currentUserGroups.some(
            (rg) => rg.role === role && rg.group === group,
          );
          if (!exists) {
            return {
              ...prev,
              userGroups: [...currentUserGroups, { role, group }],
            };
          }
        } else {
          // Remove role/group if unchecked
          return {
            ...prev,
            userGroups: currentUserGroups.filter(
              (rg) => !(rg.role === role && rg.group === group),
            ),
          };
        }

        return prev;
      });
    },
    [],
  );

  /**
   * Check if role/group is selected
   */
  const isRoleGroupSelected = useCallback(
    (role: string, group: string): boolean => {
      return (
        formData.userGroups?.some(
          (rg) => rg.role === role && rg.group === group,
        ) || false
      );
    },
    [formData.userGroups],
  );

  /**
   * Validate date/time values
   */
  const validateDateTime = useCallback(
    (
      date: Moment | null,
      time: Moment | null,
      _fieldName: "publishedDateTime" | "unpublishedDateTime",
    ): string => {
      if (!date && !time) return "";
      if (time && !date) {
        return t("EDUCATION_PAGE.DATE_REQUIRED") || "Date is required";
      }
      if (date && !moment(date).isValid()) {
        return t("EDUCATION_PAGE.INVALID_DATE") || "Invalid date";
      }
      if (time && !moment(time).isValid()) {
        return t("EDUCATION_PAGE.INVALID_TIME") || "Invalid time";
      }
      return "";
    },
    [t],
  );

  /**
   * Handle publish date change
   */
  const handlePublishDateChange = useCallback(
    (date: Moment | null) => {
      setPublishDate(date);
      const combinedDateTime = combineDateTime(date, publishTime);
      setFormData((prev) => ({
        ...prev,
        publishedDateTime: combinedDateTime,
      }));

      // Validate date and time
      const error = validateDateTime(date, publishTime, "publishedDateTime");
      setErrors((prev) => ({
        ...prev,
        publishedDateTime: error,
      }));
    },
    [publishTime, combineDateTime, validateDateTime],
  );

  /**
   * Handle publish time change
   */
  const handlePublishTimeChange = useCallback(
    (time: Moment | null) => {
      setPublishTime(time);
      const combinedDateTime = combineDateTime(publishDate, time);
      setFormData((prev) => ({
        ...prev,
        publishedDateTime: combinedDateTime,
      }));

      // Validate date and time
      const error = validateDateTime(publishDate, time, "publishedDateTime");
      setErrors((prev) => ({
        ...prev,
        publishedDateTime: error,
      }));
    },
    [publishDate, combineDateTime, validateDateTime],
  );

  /**
   * Handle unpublish date change
   */
  const handleUnpublishDateChange = useCallback(
    (date: Moment | null) => {
      setUnpublishDate(date);
      const combinedDateTime = combineDateTime(date, unpublishTime);
      setFormData((prev) => ({
        ...prev,
        unpublishedDateTime: combinedDateTime,
      }));

      // Validate date and time
      let error = validateDateTime(date, unpublishTime, "unpublishedDateTime");

      // Additional validation: unpublish date must be after publish date
      if (!error) {
        const publishMoment = toScheduleMoment(publishDate, publishTime);
        const unpublishMoment = toScheduleMoment(date, unpublishTime);
        if (
          publishMoment &&
          unpublishMoment &&
          (unpublishMoment.isBefore(publishMoment) ||
            unpublishMoment.isSame(publishMoment))
        ) {
          error =
            t("EDUCATION_PAGE.UNPUBLISH_AFTER_PUBLISH") ||
            "Unpublish date must be after publish date";
        }
      }
      setErrors((prev) => ({
        ...prev,
        unpublishedDateTime: error,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unpublishTime, publishDate, combineDateTime, validateDateTime, t],
  );

  /**
   * Handle unpublish time change
   */
  const handleUnpublishTimeChange = useCallback(
    (time: Moment | null) => {
      setUnpublishTime(time);
      const combinedDateTime = combineDateTime(unpublishDate, time);
      setFormData((prev) => ({
        ...prev,
        unpublishedDateTime: combinedDateTime,
      }));

      // Validate date and time
      let error = validateDateTime(unpublishDate, time, "unpublishedDateTime");

      // Additional validation: unpublish datetime must be after publish datetime
      if (!error) {
        const publishMoment = toScheduleMoment(publishDate, publishTime);
        const unpublishMoment = toScheduleMoment(unpublishDate, time);
        if (
          publishMoment &&
          unpublishMoment &&
          (unpublishMoment.isBefore(publishMoment) ||
            unpublishMoment.isSame(publishMoment))
        ) {
          error =
            t("EDUCATION_PAGE.UNPUBLISH_AFTER_PUBLISH") ||
            "Unpublish date/time must be after publish date/time";
        }
      }

      setErrors((prev) => ({
        ...prev,
        unpublishedDateTime: error,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      unpublishDate,
      unpublishTime,
      publishDate,
      publishTime,
      combineDateTime,
      validateDateTime,
      toScheduleMoment,
      t,
    ],
  );

  /**
   * Check if form is valid
   */
  const isFormValid = useMemo(() => {
    const hasErrors = Object.values(errors).some((error) => error !== "");
    const publishPairValid = (() => {
      if (!publishDate && !publishTime) return true;
      if (!publishDate && publishTime) return false;
      if (publishDate && !moment(publishDate).isValid()) return false;
      if (publishTime && !moment(publishTime).isValid()) return false;
      return true;
    })();
    const unpublishPairValid = (() => {
      if (!unpublishDate && !unpublishTime) return true;
      if (!unpublishDate && unpublishTime) return false;
      if (unpublishDate && !moment(unpublishDate).isValid()) return false;
      if (unpublishTime && !moment(unpublishTime).isValid()) return false;
      return true;
    })();

    const imageProvided =
      imageFile !== null ||
      (formData.imageKey && formData.imageKey.trim() !== "");

    const requiredFieldsValid =
      formData.title.trim() !== "" &&
      formData.brief.trim() !== "" &&
      formData.description.trim() !== "" &&
      formData.categoryId !== 0 &&
      imageProvided &&
      publishPairValid &&
      unpublishPairValid;

    return !hasErrors && requiredFieldsValid;
  }, [
    formData,
    errors,
    imageFile,
    publishDate,
    publishTime,
    unpublishDate,
    unpublishTime,
  ]);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      brief: "",
      description: "",
      categoryId: 0,
      status: "ACTIVE",
      publishedDateTime: "",
      unpublishedDateTime: "",
      imageKey: "",
      userGroups: [],
      sequential: "NON_SEQUENTIAL",
    });
    setPublishDate(null);
    setPublishTime(null);
    setUnpublishDate(null);
    setUnpublishTime(null);
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(undefined);
    setErrors({});
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  }, []);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(
    (success: boolean = false) => {
      if (!saving) {
        resetForm();
        onClose(success);
      }
    },
    [saving, resetForm, onClose],
  );

  /**
   * Submit form
   */
  const handleSubmit = useCallback(async () => {
    // Final validation
    const finalErrors: FormErrors = {};
    console.log(formData);
    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof CourseFormData;
      if (fieldName === "imageKey" && imageFile) {
        return;
      }
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        finalErrors[fieldName] = error;
      }
    });

    // In edit mode, imageKey might already exist, so only validate if no image at all
    if (!isEditMode && !imageFile && !formData.imageKey) {
      finalErrors.imageKey = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    } else if (isEditMode && !imageFile && !formData.imageKey) {
      // In edit mode, if no existing image and no new image, require one
      finalErrors.imageKey = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    }

    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      showToastMsg("error", t("EDUCATION_PAGE.PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    setSaving(true);
    try {
      let imageKey: string | undefined = undefined;

      // Only upload new image if a new file was selected
      if (imageFile) {
        const uploadedImageKey = await uploadImage(imageFile, formData.title);
        if (uploadedImageKey) {
          imageKey = uploadedImageKey;
        } else {
          // Image upload failed, stop submission
          setSaving(false);
          return;
        }
      }

      // Format datetime strings using combineDateTime to ensure proper format
      const formattedPublishedDateTime = combineDateTime(
        publishDate,
        publishTime,
      );
      const formattedUnpublishedDateTime = combineDateTime(
        unpublishDate,
        unpublishTime,
      );

      // Prepare form data
      const submitData: any = {
        title: formData.title,
        brief: formData.brief,
        description: formData.description,
        categoryId: formData.categoryId,
        status: formData.status,
        sequential: formData.sequential,
        publishedDateTime: formattedPublishedDateTime || null,
        unpublishedDateTime: formattedUnpublishedDateTime || null,
      };

      // Only include userGroups in create mode, not in edit mode
      if (!isEditMode) {
        // Filter out Coordinator from userGroups before sending to API
        const userGroupsWithoutCoordinator = formData.userGroups.filter(
          (rg) => !(rg.role === "COORDINATOR" && rg.group === "INTERNAL"),
        );
        submitData.userGroups = userGroupsWithoutCoordinator;
      }

      // Only include imageKey if:
      // - In create mode (always include it)
      // - In edit mode AND a new image was uploaded
      if (!isEditMode) {
        // Create mode: always include imageKey
        submitData.imageKey = imageKey || formData.imageKey;
      } else if (imageFile && imageKey) {
        // Edit mode: only include if new image was uploaded
        submitData.imageKey = imageKey;
      }
      // If in edit mode and no new image, don't include imageKey in submitData
      console.log(submitData);
      let response;
      if (isEditMode && courseId) {
        // Update existing course
        response = await services.courses.update(
          courseId.toString(),
          submitData,
        );
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.COURSE_UPDATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      } else {
        // Create new course
        response = await services.courses.add(submitData);
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.COURSE_CREATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} course:`,
        error,
      );
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  }, [
    formData,
    imageFile,
    validateField,
    handleClose,
    t,
    uploadImage,
    isEditMode,
    courseId,
    publishDate,
    publishTime,
    unpublishDate,
    unpublishTime,
    combineDateTime,
  ]);

  /**
   * Fetch course categories
   */
  const fetchCourseCategories = useCallback(async () => {
    try {
      const response = await services.lookups.listCourseCategories();
      setCourseCategories(response || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  useEffect(() => {
    fetchCourseCategories();
  }, [fetchCourseCategories]);

  /**
   * Render date and time pickers for publish
   */
  const renderPublishDateTime = () => (
    <Box
      style={{ display: "grid", gridTemplateColumns: ".7fr 1fr", gap: "1rem" }}
    >
      <Box className="standard_text_input">
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          libInstance={moment}
          locale={locale}
        >
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="DD/MM/YYYY"
            autoOk
            value={publishDate}
            onChange={handlePublishDateChange}
            invalidDateMessage=""
            className="date-picker-standard w-100"
            placeholder={t("EDUCATION_PAGE.PUBLISH_DATE")}
            minDate={minDate}
            keyboardIcon={<EventOutlinedIcon />}
            inputVariant="standard"
            InputProps={{
              classes: { root: classes.datePackerInputRoot },
            }}
            InputLabelProps={{
              style: { zIndex: 1 },
            }}
            InputAdornmentProps={{
              classes: { root: classes.datePackerInputAdornment },
            }}
          />
        </MuiPickersUtilsProvider>
      </Box>
      <Box className="standard_text_input">
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          libInstance={moment}
          locale={locale}
        >
          <KeyboardTimePicker
            disableToolbar
            variant="inline"
            format="HH:mm"
            autoOk
            value={publishTime}
            onChange={handlePublishTimeChange}
            invalidDateMessage=""
            className="date-picker-standard w-100"
            placeholder="00:00:00"
            keyboardIcon={<AccessTimeIcon />}
            inputVariant="standard"
            InputProps={{
              classes: { root: classes.datePackerInputRoot },
            }}
            InputLabelProps={{
              style: { zIndex: 1 },
            }}
            InputAdornmentProps={{
              classes: { root: classes.datePackerInputAdornment },
            }}
          />
        </MuiPickersUtilsProvider>
      </Box>
    </Box>
  );

  /**
   * Render date and time pickers for unpublish
   */
  const renderUnpublishDateTime = () => (
    <Box
      style={{ display: "grid", gridTemplateColumns: ".7fr 1fr", gap: "1rem" }}
    >
      <Box className="standard_text_input">
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          libInstance={moment}
          locale={locale}
        >
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="DD/MM/YYYY"
            autoOk
            value={unpublishDate}
            onChange={handleUnpublishDateChange}
            invalidDateMessage=""
            className="date-picker-standard w-100"
            placeholder={t("EDUCATION_PAGE.UNPUBLISH_DATE")}
            minDate={publishDate || minDate}
            keyboardIcon={<EventOutlinedIcon />}
            inputVariant="standard"
            InputProps={{
              classes: { root: classes.datePackerInputRoot },
            }}
            InputLabelProps={{
              style: { zIndex: 1 },
            }}
            InputAdornmentProps={{
              classes: { root: classes.datePackerInputAdornment },
            }}
          />
        </MuiPickersUtilsProvider>
      </Box>
      <Box className="standard_text_input">
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          libInstance={moment}
          locale={locale}
        >
          <KeyboardTimePicker
            disableToolbar
            variant="inline"
            format="HH:mm"
            autoOk
            value={unpublishTime}
            onChange={handleUnpublishTimeChange}
            className="date-picker-standard w-100"
            invalidDateMessage=""
            placeholder="00:00:00"
            keyboardIcon={<AccessTimeIcon />}
            inputVariant="standard"
            InputProps={{
              classes: { root: classes.datePackerInputRoot },
            }}
            InputLabelProps={{
              style: { zIndex: 1 },
            }}
            InputAdornmentProps={{
              classes: { root: classes.datePackerInputAdornment },
            }}
          />
        </MuiPickersUtilsProvider>
      </Box>
    </Box>
  );

  return (
    <Modal
      show={show}
      onHide={() => handleClose(false)}
      centered
      dialogClassName={dialogClassName || "modal-lg"}
    >
      <Modal.Header
        closeButton
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <Modal.Title>
          <Typography
            variant="h3"
            color="textPrimary"
            className="font-weight-bold"
          >
            {isEditMode
              ? t("EDUCATION_PAGE.EDIT_COURSE")
              : t("EDUCATION_PAGE.CREATE_COURSE")}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box className="create__course__form">
          <Box className="course__section">
            <Typography
              variant="h3"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("EDUCATION_PAGE.COURSE_INFORMATION")}
            </Typography>
          </Box>
          {/* Photo Upload Section */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.PHOTO")}*
            </Typography>
            <Box className="standard_text_input">
              <input
                ref={inputFileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />

              {imagePreview ||
              (isEditMode && formData.imageKey && !imageFile) ? (
                <Box
                  style={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <img
                    src={imagePreview || formData.imageKey}
                    alt="Course preview"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid var(--border)",
                    }}
                  />

                  <Box className="remove-image" onClick={handleRemoveImage}>
                    <img src={icons.close} alt="" width={16} height={16} />
                  </Box>
                  {uploadingImage && uploadProgress !== undefined && (
                    <Box style={{ marginTop: "8px" }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                      />
                      <Typography
                        variant="caption"
                        style={{ marginTop: "4px" }}
                      >
                        {uploadProgress}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="span"
                  onClick={handleImageClick}
                  disabled={uploadingImage}
                  startIcon={<CloudUploadIcon />}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px dashed var(--border)",
                    textTransform: "none",
                  }}
                >
                  {uploadingImage
                    ? t("EDUCATION_PAGE.UPLOADING") || "Uploading..."
                    : t("EDUCATION_PAGE.UPLOAD_IMAGE") || "Upload Image"}
                </Button>
              )}

              {formData.imageKey && !imagePreview && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: "8px" }}
                >
                  {t("EDUCATION_PAGE.IMAGE_UPLOADED") || "Image uploaded"}
                </Typography>
              )}
              <Box className="hint d-flex align-items-center justify-content-between">
                <Typography
                  variant="body2"
                  className="px-2"
                  color="textPrimary"
                >
                  280x220px JPEG, PNG Image
                </Typography>
              </Box>
              {Boolean(errors.imageKey) && (
                <Typography
                  variant="caption"
                  color="error"
                  className="px-2"
                  style={{ marginTop: "4px" }}
                >
                  {errors.imageKey}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Course Name */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.COURSE_NAME")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.COURSE_NAME")}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.TITLE,
                }}
              />

              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.title) && (
                  <Typography variant="caption" color="error">
                    {errors.title}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  className="px-2 text-right"
                  color="textPrimary"
                  style={{ flex: "auto" }}
                >
                  {formData.title.length}/{FIELD_LIMITS.TITLE}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Subtitle */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.SUBTITLE")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.SUBTITLE")}
                multiline
                name="brief"
                rows={4}
                value={formData.brief}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.SUBTITLE,
                }}
              />
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.brief) && (
                  <Typography variant="caption" color="error">
                    {errors.brief}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  className="px-2 text-right"
                  color="textPrimary"
                  style={{ flex: "auto" }}
                >
                  {formData.brief.length}/{FIELD_LIMITS.SUBTITLE}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Description */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.COURSE_DESCRIPTION")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.COURSE_DESCRIPTION")}
                multiline
                name="description"
                rows={12}
                value={formData.description}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.DESCRIPTION,
                }}
              />

              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.description) && (
                  <Typography variant="caption" color="error">
                    {errors.description}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  className="px-2 text-right"
                  color="textPrimary"
                  style={{ flex: "auto" }}
                >
                  {formData.description.length}/{FIELD_LIMITS.DESCRIPTION}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Category */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.CATEGORY")}*
            </Typography>
            <Box className="standard_text_input" style={{ width: "60%" }}>
              <FormControl className="select-input select-grey w-100">
                <Select
                  name="categoryId"
                  onChange={handleSelectChange}
                  IconComponent={ExpandMoreIcon}
                  value={formData.categoryId}
                  error={Boolean(errors.categoryId)}
                >
                  <MenuItem value={0} disabled>
                    {t("EDUCATION_PAGE.SELECT_CATEGORY")}
                  </MenuItem>
                  {courseCategories.map((value: any, index: number) => (
                    <MenuItem key={index} value={value.id}>
                      {t(value.name)}
                    </MenuItem>
                  ))}
                </Select>

                {errors.categoryId && (
                  <Typography variant="caption" color="error" className="px-2">
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          {/* Relation & Visibility Section */}
          <Box className="course__section">
            <Typography
              variant="h3"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("EDUCATION_PAGE.RELATION_AND_VISIBILITY")}
            </Typography>
          </Box>

          {/* Status */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">{t("STATUS")}</Typography>
            <Box className="standard_text_input">
              <AppSwitchComponent
                label={t("ACTIVE")}
                onChange={(e: any) => {
                  const { checked } = e.target;
                  setFormData((prev: CourseFormData) => ({
                    ...prev,
                    status: checked ? "ACTIVE" : "INACTIVE",
                  }));
                }}
                checked={formData.status === "ACTIVE"}
                name="status"
                size="small"
                color="primary"
              />
              <Typography variant="body2" className="mt-3" color="textPrimary">
                {t("EDUCATION_PAGE.IF_SWITCHED_ON_THE_COURSE_WILL_BE_VISIBLE")}
              </Typography>
            </Box>
          </Box>

          {/* Publish Date & Time */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.PUBLISH_DATE_TIME")}
            </Typography>
            <Box className="standard_text_input" style={{ width: "80%" }}>
              {renderPublishDateTime()}

              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.publishedDateTime) && (
                  <Typography variant="caption" color="error">
                    {errors.publishedDateTime}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Unpublish Date & Time */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.UNPUBLISH_DATE_TIME")}
            </Typography>
            <Box className="standard_text_input" style={{ width: "80%" }}>
              {renderUnpublishDateTime()}
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.unpublishedDateTime) && (
                  <Typography variant="caption" color="error">
                    {errors.unpublishedDateTime}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {!isEditMode && (
            <>
              <Box className="course__section">
                <Typography
                  variant="h3"
                  color="textPrimary"
                  className="font-weight-bold"
                >
                  {t("EDUCATION_PAGE.PRIVACY_AND_ACCESS")}
                </Typography>
              </Box>

              <Box className="grid-2 mb-3">
                <Typography variant="body1">
                  {t("EDUCATION_PAGE.USER_GROUP")}
                </Typography>
                <Box className="standard_text_input">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "50%",
                    }}
                  >
                    {ROLE_GROUP_OPTIONS.map((option, index) => {
                      const isCoordinator =
                        option.role === "COORDINATOR" &&
                        option.group === "INTERNAL";
                      const isSelected = isCoordinator
                        ? true
                        : isRoleGroupSelected(option.role, option.group);

                      return (
                        <AppCheckboxComponent
                          key={index}
                          label={t(option.label)}
                          checked={isSelected}
                          color="primary"
                          size="small"
                          onChange={(e) => {
                            if (!isCoordinator) {
                              handleRoleGroupChange(
                                option.role,
                                option.group,
                                e.target.checked,
                              );
                            }
                          }}
                          disabled={isCoordinator}
                        />
                      );
                    })}
                  </Box>
                  <Typography
                    variant="body2"
                    className="px-2"
                    color="textPrimary"
                  >
                    {t("EDUCATION_PAGE.WHO_CAN_SEE_THIS_COURSE")}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="px-2"
                    color="textPrimary"
                  >
                    {t(
                      "EDUCATION_PAGE.DECIRE_WHICH_GROUP_OF_USERS_CAN_SEE_THIS_COURSE",
                    )}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          <Box className="course__section">
            <Typography
              variant="h3"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("EDUCATION_PAGE.WORKFLOW")}
            </Typography>
          </Box>
          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.UNLOCK_SECTIONS")}
            </Typography>
            <Box className="section-workflow">
              <AppRadioComponent
                name="workflowSections"
                value={formData.sequential}
                onChange={(value: string) =>
                  setFormData((prev: CourseFormData) => ({
                    ...prev,
                    sequential: value,
                  }))
                }
                options={[
                  {
                    value: "NON_SEQUENTIAL",
                    label: (
                      <Typography variant="body1" color="textPrimary">
                        {t("EDUCATION_PAGE.NONE")}
                        <br />
                        {t(
                          "EDUCATION_PAGE.THE_UNIT_WILL_BE_UNLOCKED_AND_ALL_MEMBERS_WILL_GAIN_ACCESS",
                        )}
                      </Typography>
                    ),
                  },
                  {
                    value: "SEQUENTIAL",
                    label: (
                      <Typography variant="body1" color="textPrimary">
                        {t("EDUCATION_PAGE.SEQUENTIAL")}
                        <br />
                        {t(
                          "EDUCATION_PAGE.THE_UNIT_WILL_BE_UNLOCKED_IMMEDIATELY_AFTER_THE_PREVIOUS_ELEMENT_HAS_BEEN_COMPLETED",
                        )}
                      </Typography>
                    ),
                  },
                ]}
              />
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box className="d-flex align-items-center" style={{ gap: "1rem" }}>
          <AppButtonComponent
            variant="text"
            color="secondary"
            onClick={() => handleClose(false)}
            disabled={saving}
          >
            {t("CANCEL")}
          </AppButtonComponent>
          <AppButtonComponent
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isFormValid || saving}
          >
            <CheckIcon /> {isEditMode ? t("UPDATE") : t("CREATE")}
          </AppButtonComponent>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCourseDialog;
